import { type ComponentPropsWithRef, type ElementType, forwardRef, Ref, RefAttributes, ReactNode } from "react";

/*
 * `DistributiveOmit` is a utility type that removes specific keys from each type in a union.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

/**
 * `PolymorphicProps` is a utility type that enables polymorphic components to accept different element types while omitting the `as` prop.
 *
 * @template TAs - The element type provided as a prop to the component.
 * @template TDefaultElement - The default element type of the component.
 *
 * @typeParam TAs - The element type that the component should render, if specified.
 * @typeParam TDefaultElement - The default element type that the component will render if `TAs` is not specified.
 *
 * @returns A type that represents the props of the component with the specified element type (`TAs` or `TDefaultElement`),
 * excluding the `as` prop.
 *
 * @example
 * ```typescript
 * type ButtonProps = PolymorphicProps<'a', 'button'>;
 * // ButtonProps will have props of an anchor element, excluding the 'as' prop.
 * ```
 */
export type PolymorphicProps<TAs extends ElementType, TDefaultElement extends ElementType> = DistributiveOmit<
    ComponentPropsWithRef<ElementType extends TAs ? TDefaultElement : TAs>,
    "as"
>;

/*
 * Defines a polymorphic function type for creating forward refs with props and ref handling.
 */
type ForwardRefPolymorphic = <TRef, TProps = object>(
    render: (props: TProps, ref: Ref<TRef>) => ReactNode
) => (props: TProps & RefAttributes<TRef>) => ReactNode;

/**
 * A utility function that creates a forward ref component with polymorphic support,
 * allowing it to accept a variety of props and refs.
 *
 * This function is a type-safe wrapper around `React.forwardRef` with enhanced polymorphism.
 *
 * @example
 * ```typescript
 * // InnerHeading is a component that accepts a polymorphic `as` prop.
 * const Heading = forwardRefPolymorphic(InnerHeading);
 * ```
 */
export const forwardRefPolymorphic = forwardRef as ForwardRefPolymorphic;
